import {
  DATA_TYPES,
  MAX_CLASSES_FOR_NUMBER,
  MAX_CLASSES_PER_COL,
  MIN_FREQ_CLASS,
} from "../constants";
import {
  calcAverage,
  calcMax,
  calcMin,
  calcPercentile,
  convertToNumber,
  isNaN2,
  isNumber,
} from "../helpers/DataHelpers";
import { frequencyCounts } from "../helpers/FrequencyCounts";

export type colStatsType = {
  mean: number;
  median: number;
  q25: number;
  q75: number;
  min: number;
  max: number;
  mostCommon: string;
  leastCommon: string;
};

export default class Column {
  columnName: string;
  availableColumnTypes: string[]; // Some columns can be both numeric and categorical
  columnType: string; // Currently selected column type
  allClasses: string[];
  nonSparseClasses: string[];
  colStats: colStatsType;
  rawFrequency: Map<string, number>;
  vectorizedData: number[];
  dataWithOutliers: number[];

  constructor(
    data: string[],
    columnName: string,
    colTemplate?: Column,
    availableColTypes?: string[]
  ) {
    if (data === undefined) {
      return;
    }
    this.columnName = columnName;

    // Determine the column type
    this.rawFrequency = frequencyCounts(data);

    if (colTemplate !== undefined) {
      this.availableColumnTypes = colTemplate.availableColumnTypes;
    } else if (availableColTypes !== undefined) {
      this.availableColumnTypes = availableColTypes;
    } else {
      this.availableColumnTypes = this.determineColumnType(data, columnName);
    }

    this.columnType = this.availableColumnTypes[0];

    // Handle case where trailing zeros are cutoff
    if (
      this.columnType === DATA_TYPES.categorical &&
      this.availableColumnTypes.includes(DATA_TYPES.number)
    ) {
      let rafFreq: Map<string, number> = new Map();
      for (let i = 0; i < data.length; i++) {
        data[i] = "" + convertToNumber(data[i]);
      }
      for (let key of Object.keys(this.rawFrequency)) {
        if (key.includes(".")) {
          rafFreq["" + convertToNumber(key)] = this.rawFrequency[key];
        } else {
          rafFreq[key] = this.rawFrequency[key];
        }
      }
      this.rawFrequency = rafFreq;
    }

    this.allClasses = Object.keys(this.rawFrequency);
    if (this.columnType === DATA_TYPES.unusable) {
      this.rawFrequency = new Map();
      this.allClasses = [];
    }

    this.processData(data, colTemplate);
  }

  processData(data: string[], colTemplate?: Column) {
    if (colTemplate === undefined) {
      let nonSparse = Array.from(this.calcNonSparseClasses()).sort();
      if (
        this.columnType === DATA_TYPES.categorical &&
        this.availableColumnTypes.includes(DATA_TYPES.number)
      ) {
        let nums: number[] = [];
        let nonNums: string[] = [];
        for (let val of nonSparse) {
          let val2 = convertToNumber(val);
          if (isNaN2(val2)) {
            nonNums.push(val);
          } else {
            nums.push(val2);
          }
        }
        nonSparse = [
          ...nums.sort((a, b) => a - b).map((val) => val + ""),
          ...nonNums.sort(),
        ];
      }
      this.nonSparseClasses = nonSparse;
    } else {
      this.nonSparseClasses = colTemplate.nonSparseClasses;
    }

    // Convert data to number array
    let numberArray = this.convertDataToNumberArray(data);
    if (colTemplate === undefined) {
      this.colStats = this.calcColumnStats(numberArray);
    } else {
      this.colStats = colTemplate.colStats;
    }
    this.dataWithOutliers = [...numberArray];
    this.vectorizedData = this.cleanData(numberArray);
  }

  determineColumnType(data: string[], columnName: string) {
    // Gather stats about the column

    const dataFiltered = data.filter((val) => val !== "" && val !== undefined);
    const numDataPoints = dataFiltered.length;
    const numberCount = dataFiltered
      .map(isNumber)
      .reduce((a, b) => a + (b ? 1 : 0), 0);
    const uniqueCount = new Set(dataFiltered).size;
    const decimalCount = dataFiltered
      .map((val) => val.includes("."))
      .reduce((a, b) => a + (b ? 1 : 0), 0);
    const integerCount = dataFiltered
      .map(convertToNumber)
      .map(Number.isInteger)
      .reduce((a, b) => a + (b ? 1 : 0), 0);
    const classWithMin = Object.values(this.rawFrequency).filter(
      (val) => val >= dataFiltered.length * MIN_FREQ_CLASS
    );
    let colNameLower = columnName.toLowerCase();
    let colTypes: string[] = [];
    // Too little data
    if (numDataPoints <= 30) {
      colTypes.push(DATA_TYPES.unusable);
      return colTypes;
    }
    // Potentially categorical
    if (numberCount / numDataPoints < 0.5) {
      if (uniqueCount / numDataPoints >= 0.9 || !classWithMin) {
        colTypes.push(DATA_TYPES.unusable);
      } else if (uniqueCount > 20) {
        colTypes.push(DATA_TYPES.unusable);
      } else {
        colTypes.push(DATA_TYPES.categorical);
      }
      if (uniqueCount <= 1) {
        return [DATA_TYPES.unusable];
      }
      return colTypes;
    } // Potentially numeric
    else {
      if (uniqueCount <= MAX_CLASSES_FOR_NUMBER) {
        if (integerCount / numDataPoints > 0.8) {
          colTypes.push(DATA_TYPES.categorical);
        }
      } else {
        if (
          // ID column
          (colNameLower.startsWith("id") || colNameLower.endsWith("id")) &&
          uniqueCount / numDataPoints >= 0.9
        ) {
          colTypes.push(DATA_TYPES.unusable);
        }
      }
      colTypes.push(DATA_TYPES.number);
      if (uniqueCount <= 1) {
        return [DATA_TYPES.unusable];
      }
      return colTypes;
    }
  }

  convertDataToNumberArray(data: string[]) {
    const convFn =
      this.columnType == DATA_TYPES.number
        ? Column.processNumberColumn.bind(this)
        : this.processCategoricalColumn.bind(this);
    return convFn(data);
  }

  static processNumberColumn(data: string[]) {
    let arr: number[] = [];
    for (let val of data) {
      let entry = convertToNumber(val);
      if (!isNumber(val)) {
        entry = NaN;
      }
      arr.push(entry);
    }
    return arr;
  }

  calcNonSparseClasses() {
    let nonSpares: Set<string> = new Set();
    if (this.columnType !== DATA_TYPES.categorical) {
      return nonSpares;
    }
    const total = Object.values(this.rawFrequency).reduce((b, a) => b + a, 0);
    for (const [key, value] of Object.entries(this.rawFrequency)) {
      if (value > MIN_FREQ_CLASS * total || value >= 5) {
        nonSpares.add(key);
      }
    }

    // Set max number of classes
    if (nonSpares.size > MAX_CLASSES_PER_COL) {
      let sorted = Object.entries(this.rawFrequency)
        .sort((first, second) => (first[0] > second[0] ? 1 : 0))
        .slice(0, MAX_CLASSES_PER_COL);
      let classesToKeep: Set<string> = new Set(sorted.map((val) => val[0]));
      for (let entry of Array.from(nonSpares)) {
        if (!classesToKeep.has(entry) && nonSpares.has(entry)) {
          nonSpares.delete(entry);
        }
      }
    }

    return nonSpares;
  }

  processCategoricalColumn(data: string[]) {
    let arr: number[] = [];
    for (let val of data) {
      let idx = this.getClassIndex(val);
      arr.push(idx);
    }
    return arr;
  }

  getClassIndex(classValue: string) {
    let nonSparseClasses = Array.from(this.nonSparseClasses);
    let idx = nonSparseClasses.length;
    let nonSparse = new Set(this.nonSparseClasses);
    if (nonSparse.has(classValue)) {
      idx = nonSparseClasses.indexOf(classValue);
    }
    return idx;
  }
  getClassByIndex(index: number, returnOther = false) {
    let classVal = Array.from(this.nonSparseClasses)[index];
    if (classVal === undefined) {
      if (returnOther) {
        return "Other Classes";
      }
      //Find most common sparse class
      let maxVal = 0;
      for (const [key, val] of Object.entries(this.rawFrequency)) {
        if (val > maxVal) {
          classVal = key;
          maxVal = val;
        }
      }
    }
    return classVal;
  }

  cleanData(data: number[]) {
    if (this.columnType == DATA_TYPES.categorical) {
      return data;
    }
    // data = this.removeOutliers(data);
    data = Column.removeNaNs(data, this.colStats.median);
    return data;
  }
  removeOutliers(data: number[]) {
    // Calc qurrtile cutoffs
    const q25 = this.colStats.q25;
    const q75 = this.colStats.q75;
    const topCutoff = q75 + (q75 - q25) * 3;
    const bottomCutoff = q25 - (q75 - q25) * 3;

    for (let i = 0; i < data.length; i++) {
      if (isNaN2(data[i])) {
        continue;
      }
      if (data[i] > topCutoff) {
        data[i] = topCutoff;
      } else if (data[i] < bottomCutoff) {
        data[i] = bottomCutoff;
      }
    }
    return data;
  }
  static removeNaNs(data: number[], replaceWith) {
    return data.map((val) =>
      isNaN2(val) || val === undefined ? replaceWith : val
    );
  }
  calcColumnStats(data: number[]) {
    const data_filtered = data.filter((val) => !isNaN2(val));
    let commons = this.calcMostCommon();

    const columnStats: colStatsType = {
      mean: calcAverage(data_filtered, this.columnType),
      median: calcPercentile(data_filtered, 0.5, this.columnType),
      q25: calcPercentile(data_filtered, 0.25, this.columnType),
      q75: calcPercentile(data_filtered, 0.75, this.columnType),
      min: calcMin(data_filtered, this.columnType),
      max: calcMax(data_filtered, this.columnType),
      mostCommon: commons.mostCommon,
      leastCommon: commons.leastCommon,
    };
    return columnStats;
  }

  calcMostCommon() {
    let minCount = 3;
    let mostCommon = "-";
    let leastCommon = "-";
    let minCount2 = 10e10;
    for (const [key, value] of Object.entries(this.rawFrequency)) {
      if (value > minCount && key.length > 0) {
        minCount = value as number;
        mostCommon = key;
      }
      if (value < minCount2 && key.length > 0) {
        minCount2 = value as number;
        leastCommon = key;
      }
    }
    return { mostCommon, leastCommon };
  }
  static getNClasses(col: Column) {
    let nClasses = 1;
    if (col.columnType === DATA_TYPES.categorical) {
      nClasses =
        col.nonSparseClasses.length +
        (col.allClasses.length > col.nonSparseClasses.length ? 1 : 0);
    }
    return nClasses;
  }
}
